import { Injectable, Injector, runInInjectionContext } from '@angular/core';
import { AuthError } from '../models/auth-error.model';
import { UserFilterPreferences } from '../models/user-filter-preferences.model';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { Auth, signInWithEmailAndPassword, User, createUserWithEmailAndPassword, confirmPasswordReset, verifyPasswordResetCode, sendPasswordResetEmail } from '@angular/fire/auth';
import { Firestore, doc, setDoc, getDoc, onSnapshot, collection, addDoc, query, where, getDocs } from '@angular/fire/firestore';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { adminRoles } from '../constants/constants';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { RecaptchaService } from './recaptcha.service';
import { LoggerService } from './logger.service';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  private userRolesSubject: BehaviorSubject<string[]>;
  private userCountrySubject = new BehaviorSubject<string>('');
  private userFilterPreferencesSubject = new BehaviorSubject<UserFilterPreferences>({
    affiliation: '', fieldOfStudy: '', focusAreas: []
  });

  currentUser$: Observable<User | null>;
  userRoles$: Observable<string[]>;
  isLoggedIn$: Observable<boolean>;
  readonly userCountry$ = this.userCountrySubject.asObservable();
  readonly userFilterPreferences$ = this.userFilterPreferencesSubject.asObservable();

  private userStatusSubscription: any;

  constructor(
    private auth: Auth,
    private router: Router,
    private firestore: Firestore,
    private recaptchaV3Service: ReCaptchaV3Service,
    private recaptchaService: RecaptchaService,
    private logger: LoggerService,
    private sessionService: SessionService,
    private injector: Injector
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
    this.userRolesSubject = new BehaviorSubject<string[]>(this.getRolesFromStorage());
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.userRoles$ = this.userRolesSubject.asObservable();
    this.isLoggedIn$ = this.currentUser$.pipe(map(user => !!user));

    this.auth.onAuthStateChanged(async (user) => {
      runInInjectionContext(this.injector, async () => {
        this.setUserInStorage(user);
        this.resetFilterPreferences();
        this.currentUserSubject.next(user);
        if (user) {
          await this.updateUserRoles(user.uid);
          await this.sessionService.startSession();
          this.startUserStatusListener(user.uid);
        } else {
          this.setRolesInStorage([]);
          this.userRolesSubject.next([]);
          this.stopUserStatusListener();
        }
      });
    });
  }

  async getUserDocument(uid: string): Promise<any> {
    return runInInjectionContext(this.injector, async () => {
      const userDoc = await getDoc(doc(this.firestore, 'Users', uid));
      return userDoc.exists() ? userDoc.data() : null;
    });
  }

  private startUserStatusListener(uid: string): void {
    this.stopUserStatusListener();
    const userRef = doc(this.firestore, 'Users', uid);
    runInInjectionContext(this.injector, () => {
      this.userStatusSubscription = onSnapshot(userRef, async (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          this.userCountrySubject.next(typeof userData['country'] === 'string' ? userData['country'] : '');
          this.userFilterPreferencesSubject.next({
            affiliation: typeof userData['affiliation'] === 'string' ? userData['affiliation'] : '',
            fieldOfStudy: typeof userData['fieldOfStudy'] === 'string' ? userData['fieldOfStudy'] : '',
            focusAreas: Array.isArray(userData['focusAreas'])
              ? userData['focusAreas'].filter((area: unknown): area is string => typeof area === 'string')
              : []
          });
          if (userData['isActive'] === false) {
            await this.logout();
            this.router.navigate(['/']);
          }
        }
      });
    });
  }

  private stopUserStatusListener(): void {
    if (this.userStatusSubscription) {
      this.userStatusSubscription();
      this.userStatusSubscription = null;
    }
  }

  private resetFilterPreferences(): void {
    this.userCountrySubject.next('');
    this.userFilterPreferencesSubject.next({ affiliation: '', fieldOfStudy: '', focusAreas: [] });
  }

  async login(email: string, password: string): Promise<void> {
    try {
      this.recaptchaV3Service.execute('login')
        .subscribe(
          (token) => this.handleToken(token, 'login'),
          (error) => console.error('reCAPTCHA error:', error)
        );

      const userCredential = await runInInjectionContext(this.injector, () =>
        signInWithEmailAndPassword(this.auth, email, password)
      );

      const isActive = await this.checkUserActive(userCredential.user.uid);
      if (!isActive) {
        await this.logout();
        throw new AuthError('auth/user-inactive', 'Your account has been deactivated. Please contact support for assistance.');
      }

      await this.updateUserRoles(userCredential.user.uid);
      const roles = await this.getCurrentUserRoles();

      this.logger.setUserId(userCredential.user.uid);
      this.logger.setEmail(email);
      this.logger.addLogItem('LOGIN', 'info');

      if (roles.some(role => adminRoles.includes(role))) {
        this.router.navigate(['/admin']);
      } else {
        this.router.navigate(['/']);
      }
    } catch (error: any) {
      this.logger.addLogItem('LOGIN_ERROR', JSON.stringify({
        error: this.getErrorMessage(error),
        email: email,
      }), 'error');
      throw new Error(this.getErrorMessage(error));
    } finally {
      if (!this.auth.currentUser) {
        this.setUserInStorage(null);
        this.setRolesInStorage([]);
        this.userRolesSubject.next([]);
        this.resetFilterPreferences();
        this.currentUserSubject.next(null);
      }
    }
  }

  private async getCurrentUserRoles(): Promise<string[]> {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return [];

    return runInInjectionContext(this.injector, async () => {
      const userDoc = await getDoc(doc(this.firestore, 'Users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData['roles'] || [];
      }
      return [];
    });
  }
  private handleToken(token: string, action: string) {
    this.recaptchaService.verifyToken(token, action).subscribe(
      (response) => {
        if (response.tokenProperties.valid && response.riskAnalysis.score > 0.5) {
        } else {
          return;
        }
      },
      (error) => console.error('Token verification error:', error)
    );
  }

  private async checkUserActive(uid: string): Promise<boolean> {
    return runInInjectionContext(this.injector, async () => {
      const userDoc = await getDoc(doc(this.firestore, 'Users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData['isActive'] === true;
      }
      return false;
    });
  }

  private getErrorMessage(error: any): string {
    if (error instanceof AuthError) {
      return error.message;
    }
    if (error.code) {
      switch (error.code) {
        case 'auth/invalid-email':
          return 'Invalid email address.';
        case 'auth/user-disabled':
          return 'This account has been disabled.';
        case 'auth/user-inactive':
          return 'Your account has been deactivated. Please contact support for assistance.';
        case 'auth/user-not-found':
          return 'User does not exist.';
        case 'auth/wrong-password':
          return 'Invalid email or password.';
        case 'auth/invalid-credential':
          return 'Invalid email or password.';
        case 'auth/email-already-in-use':
          return 'This email address is already registered. Please use a different email or try logging in.';
      }
    }
    if (error.message && (
      error.message.includes('reCAPTCHA') ||
      error.message.includes('Security check')
    )) {
      return error.message;
    }
    if (error.status === 400) {
      return 'Invalid password.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  async logout(): Promise<void> {
    try {
      this.resetFilterPreferences();
      this.currentUserSubject.next(null);
      this.userRolesSubject.next([]);

      localStorage.removeItem('currentUser');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('lastSessionAction');

      await this.auth.signOut();
      this.router.navigate(['/']);
    } catch (error) {
      console.error('Logout failed', error);
    }
  }

  async register(email: string, password: string, userData: any, recaptchaToken: string): Promise<void> {
    try {
      const userCredential = await runInInjectionContext(this.injector, () =>
        createUserWithEmailAndPassword(this.auth, email, password)
      );

      const uid = userCredential.user.uid;

      await runInInjectionContext(this.injector, () =>
        setDoc(doc(this.firestore, 'Users', uid), { ...userData, id: uid, isActive: true })
      );

      await this.updateUserRoles(uid);
      this.logger.setUserId(uid);
      this.logger.setEmail(email);
      this.logger.addLogItem('REGISTRATION_SUCCESSFUL', JSON.stringify({ userId: uid, email: email }), 'info');
      await this.sessionService.startSession();
    } catch (error) {
      this.logger.addLogItem('REGISTRATION_ERROR', JSON.stringify({ error: this.getErrorMessage(error), email: email }), 'error');
      console.error('Registration error:', error);
      throw error;
    }
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  private async updateUserRoles(uid: string): Promise<void> {
    try {
      const roles = await runInInjectionContext(this.injector, async () => {
        const userDoc = await getDoc(doc(this.firestore, 'Users', uid));
        let foundRoles: string[] = [];
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData && Array.isArray(userData['roles'])) {
            foundRoles = userData['roles'] as string[];
          }
        }
        return foundRoles;
      });

      this.setRolesInStorage(roles);
      this.userRolesSubject.next(roles);
    } catch (error) {
      this.setRolesInStorage([]);
      this.userRolesSubject.next([]);
    }
  }

  logCurrentUserRoles(): Observable<string[]> {
    return this.userRoles$;
  }

  private getUserFromStorage(): User | null {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  }

  private setUserInStorage(user: User | null): void {
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('currentUser');
    }
  }

  private getRolesFromStorage(): string[] {
    const rolesStr = localStorage.getItem('userRoles');
    return rolesStr ? JSON.parse(rolesStr) : [];
  }

  private setRolesInStorage(roles: string[]): void {
    localStorage.setItem('userRoles', JSON.stringify(roles));
  }

  getRemainingTime(): Observable<string> {
    return this.sessionService.getRemainingTime();
  }

  refreshSession(): void {
    this.sessionService.refreshSession();
  }

  shouldWarnUser(): Observable<boolean> {
    return this.sessionService.shouldWarnUser();
  }

  resetPassword(email: string): Observable<void> {
    return from(this.createPasswordResetRequest(email))
      .pipe(
        catchError(error => {
          console.error('Hiba a jelszó visszaállítása során:', error);
          throw error;
        })
      );
  }

  private async createPasswordResetRequest(email: string): Promise<void> {
    try {
      const usersRef = collection(this.firestore, 'Users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        throw new Error('EMAIL_NOT_FOUND');
      }
      const userDoc = querySnapshot.docs[0];
      const userId = userDoc.id;
      const name = userDoc.data()['name'] || null;
      await addDoc(collection(this.firestore, 'PasswordResetRequests'), {
        email,
        userId,
        name,
        createdAt: new Date().toISOString(),
        status: 'pending'
      });
    } catch (error) {
      console.error('Hiba a jelszó visszaállítási kérés létrehozása során:', error);
      throw error;
    }
  }

  confirmPasswordReset(actionCode: string, newPassword: string): Observable<void> {
    return from(confirmPasswordReset(this.auth, actionCode, newPassword))
      .pipe(
        catchError(error => {
          console.error('Hiba a jelszó megerősítése során:', error);
          throw error;
        })
      );
  }

  verifyPasswordResetCode(actionCode: string): Observable<any> {
    return from(verifyPasswordResetCode(this.auth, actionCode))
      .pipe(
        catchError(error => {
          console.error('Érvénytelen vagy lejárt kód:', error);
          throw error;
        })
      );
  }
}
